import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { communityApi } from '../lib/api';
import { ArrowLeft, UserPlus, UserMinus, Edit3, Check, X, MessageSquare, Heart, Activity, Send, Trash2, Loader2 } from 'lucide-react';

const ACTIVITY_LABELS = {
  post: 'shared a post',
  training_log: 'logged a workout',
  milestone: 'hit a milestone',
  meal_plan_share: 'shared a meal plan',
  follow: 'started following',
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function CommentsSection({ activityId, commentsCount, token, user }) {
  const [comments, setComments] = useState([]);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [count, setCount] = useState(commentsCount || 0);

  async function loadComments() {
    if (!open) {
      setOpen(true);
      setLoading(true);
      try {
        const res = await communityApi.getComments(activityId);
        setComments(res.data.comments || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    } else { setOpen(false); }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!text.trim() || !token) return;
    setPosting(true);
    try {
      const res = await communityApi.addComment(activityId, text.trim(), token);
      setComments(prev => [...prev, res.data]);
      setCount(prev => prev + 1);
      setText('');
    } catch (e) { console.error(e); }
    setPosting(false);
  }

  async function handleDelete(commentId) {
    try {
      await communityApi.deleteComment(commentId, token);
      setComments(prev => prev.filter(c => c.comment_id !== commentId));
      setCount(prev => Math.max(0, prev - 1));
    } catch (e) { console.error(e); }
  }

  return (
    <div>
      <button onClick={loadComments} className="flex items-center gap-1.5 text-zinc-500 text-xs hover:text-zinc-300 transition-colors" data-testid={`toggle-comments-${activityId}`}>
        <MessageSquare className="w-3.5 h-3.5" />
        {count > 0 ? `${count} comment${count !== 1 ? 's' : ''}` : 'Comment'}
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          {loading ? (
            <div className="flex items-center gap-2 text-zinc-600 text-xs"><Loader2 className="w-3 h-3 animate-spin" /> Loading...</div>
          ) : (
            <>
              {comments.map(c => (
                <div key={c.comment_id} className="flex gap-2 group" data-testid={`comment-${c.comment_id}`}>
                  <Link to={`/community/${c.username}`} className="flex-shrink-0">
                    <div className="w-7 h-7 bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-500 text-[10px] font-bold uppercase" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                      {c.avatar ? <img src={c.avatar} alt="" className="w-full h-full object-cover" /> : c.display_name?.charAt(0) || '?'}
                    </div>
                  </Link>
                  <div className="flex-1 min-w-0 bg-zinc-800/40 px-3 py-2 border border-zinc-800/60">
                    <div className="flex items-center gap-2">
                      <Link to={`/community/${c.username}`} className="text-white text-xs font-medium hover:text-[#00F0FF] transition-colors">{c.display_name}</Link>
                      <span className="text-zinc-600 text-[10px]">{timeAgo(c.created_at)}</span>
                      {user && c.user_id === user.user_id && (
                        <button onClick={() => handleDelete(c.comment_id)} className="ml-auto opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-red-400 transition-all">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <p className="text-zinc-400 text-xs mt-0.5 leading-relaxed">{c.content}</p>
                  </div>
                </div>
              ))}
              {user && token && (
                <form onSubmit={handleSubmit} className="flex gap-2">
                  <input value={text} onChange={e => setText(e.target.value)} placeholder="Write a comment..." maxLength={1000}
                    className="flex-1 bg-zinc-800/40 border border-zinc-700 text-white text-xs px-3 py-2 placeholder-zinc-600 focus:outline-none focus:border-zinc-500" />
                  <button type="submit" disabled={posting || !text.trim()} className="px-3 py-2 bg-[#E63946] text-white disabled:opacity-40 hover:bg-[#E63946]/80 transition-colors">
                    <Send className="w-3.5 h-3.5" />
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ProfileActivityCard({ activity, token, user }) {
  const [liked, setLiked] = useState(activity.liked_by_me || false);
  const [likesCount, setLikesCount] = useState(activity.likes_count || 0);

  async function handleLike() {
    if (!token) return;
    try {
      const res = await communityApi.toggleLike(activity.activity_id, token);
      setLiked(res.data.status === 'liked');
      setLikesCount(res.data.likes_count);
    } catch (e) { console.error(e); }
  }

  return (
    <div className="bg-zinc-900/60 border border-zinc-800 p-5" data-testid={`profile-activity-${activity.activity_id || ''}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-zinc-500 text-xs">{ACTIVITY_LABELS[activity.type] || 'posted'}</span>
        <span className="text-zinc-600 text-xs ml-auto">{timeAgo(activity.created_at)}</span>
      </div>
      {activity.title && <h4 className="text-white text-sm font-medium" style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: '1rem' }}>{activity.title}</h4>}
      {activity.content && <p className="text-zinc-400 text-sm mt-1 leading-relaxed">{activity.content}</p>}

      {activity.images && activity.images.length > 0 && (
        // eslint-disable-next-line no-nested-ternary -- readable tier picker
        <div className={`mt-3 grid gap-2 ${activity.images.length === 1 ? 'grid-cols-1' : activity.images.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
          {activity.images.map((img, i) => (
            <div key={img} className="aspect-square bg-zinc-800 border border-zinc-700 overflow-hidden">
              <img src={communityApi.getImageUrl(img)} alt="" className="w-full h-full object-cover" loading="lazy" />
            </div>
          ))}
        </div>
      )}

      {activity.activity_id && activity.type !== 'follow' && (
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-zinc-800/60">
          <button onClick={handleLike} disabled={!token}
            className={`flex items-center gap-1.5 text-xs transition-colors ${liked ? 'text-[#E63946]' : 'text-zinc-500 hover:text-[#E63946]'} ${!token ? 'opacity-40 cursor-not-allowed' : ''}`}
            data-testid={`like-btn-${activity.activity_id}`}
          >
            <Heart className={`w-3.5 h-3.5 ${liked ? 'fill-[#E63946]' : ''}`} />
            {likesCount > 0 && likesCount}
          </button>
          <CommentsSection activityId={activity.activity_id} commentsCount={activity.comments_count || 0} token={token} user={user} />
        </div>
      )}
    </div>
  );
}

export default function CommunityProfilePage() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const isMe = username === 'me';

  const [profile, setProfile] = useState(null);
  const [activities, setActivities] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('posts');
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ bio: '', display_name: '' });
  const [isFollowing, setIsFollowing] = useState(false);
  const [isOwnProfile, setIsOwnProfile] = useState(false);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      let profileRes;
      if (isMe) {
        if (!token) { navigate('/auth'); return; }
        profileRes = await communityApi.getMyProfile(token);
      } else {
        profileRes = await communityApi.getProfile(username, token);
      }
      const p = profileRes.data;
      setProfile(p);
      setIsFollowing(p.is_following || false);
      setIsOwnProfile(p.is_own_profile || isMe);
      setEditForm({ bio: p.bio || '', display_name: p.display_name || '' });

      const uname = p.username;
      const [feedRes, followersRes, followingRes] = await Promise.all([
        communityApi.getUserFeed(uname, token).catch(() => ({ data: { feed: [] } })),
        communityApi.getFollowers(uname).catch(() => ({ data: { followers: [] } })),
        communityApi.getFollowing(uname).catch(() => ({ data: { following: [] } })),
      ]);
      setActivities(feedRes.data.feed || []);
      setFollowers(followersRes.data.followers || []);
      setFollowing(followingRes.data.following || []);
    } catch (e) { console.error('Profile load error:', e); }
    setLoading(false);
  }, [username, token, isMe, navigate]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  async function handleFollow() {
    if (!token || !profile) return;
    try {
      if (isFollowing) {
        await communityApi.unfollow(profile.username, token);
        setIsFollowing(false);
        setProfile(prev => ({ ...prev, followers_count: Math.max(0, (prev.followers_count || 1) - 1) }));
      } else {
        await communityApi.follow(profile.username, token);
        setIsFollowing(true);
        setProfile(prev => ({ ...prev, followers_count: (prev.followers_count || 0) + 1 }));
      }
    } catch (e) { console.error('Follow error:', e); }
  }

  async function handleSaveProfile() {
    if (!token) return;
    try {
      const res = await communityApi.updateProfile(editForm, token);
      setProfile(res.data);
      setEditing(false);
    } catch (e) { console.error('Update error:', e); }
  }

  if (loading) {
    return (
      <main className="pt-20 min-h-screen">
        <div className="container-ninja section-spacing">
          <div className="animate-pulse space-y-4"><div className="h-8 bg-zinc-800 w-1/3" /><div className="h-32 bg-zinc-800" /><div className="h-64 bg-zinc-800" /></div>
        </div>
      </main>
    );
  }

  if (!profile) {
    return (
      <main className="pt-20 min-h-screen" data-testid="profile-not-found">
        <div className="container-ninja section-spacing text-center">
          <h1 className="text-white text-3xl mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Profile Not Found</h1>
          <p className="text-zinc-400 mb-6">This athlete hasn't joined the dojo yet.</p>
          <Link to="/community" className="btn-ninja inline-flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Back to Community</Link>
        </div>
      </main>
    );
  }

  const tabs = [
    { id: 'posts', label: 'Activity', count: profile.posts_count || 0 },
    { id: 'followers', label: 'Followers', count: profile.followers_count || 0 },
    { id: 'following', label: 'Following', count: profile.following_count || 0 },
  ];

  return (
    <main className="pt-20 min-h-screen" data-testid="community-profile-page">
      <div className="container-ninja section-spacing max-w-4xl mx-auto">
        <Link to="/community" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8" data-testid="back-to-community">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm uppercase tracking-wider" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Community</span>
        </Link>

        {/* Profile Header */}
        <div className="bg-zinc-900/60 border border-zinc-800 p-6 md:p-8 mb-6" data-testid="profile-header">
          <div className="flex flex-col sm:flex-row items-start gap-5">
            <div className="w-20 h-20 bg-zinc-800 border-2 border-zinc-700 flex items-center justify-center text-zinc-400 text-3xl font-bold uppercase flex-shrink-0" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              {profile.avatar ? <img src={profile.avatar} alt="" className="w-full h-full object-cover" /> : profile.display_name?.charAt(0) || '?'}
            </div>

            <div className="flex-1 min-w-0">
              {editing ? (
                <div className="space-y-3">
                  <input value={editForm.display_name} onChange={e => setEditForm(prev => ({ ...prev, display_name: e.target.value }))}
                    className="w-full bg-zinc-800/50 border border-zinc-700 text-white text-lg px-3 py-2 placeholder-zinc-600 focus:outline-none focus:border-zinc-500"
                    placeholder="Display Name" data-testid="edit-display-name" style={{ fontFamily: 'Bebas Neue, sans-serif' }} />
                  <textarea value={editForm.bio} onChange={e => setEditForm(prev => ({ ...prev, bio: e.target.value }))} rows={2}
                    className="w-full bg-zinc-800/50 border border-zinc-700 text-white text-sm px-3 py-2 placeholder-zinc-600 focus:outline-none focus:border-zinc-500 resize-none"
                    placeholder="Write a short bio..." data-testid="edit-bio" />
                  <div className="flex gap-2">
                    <button onClick={handleSaveProfile} className="px-4 py-2 bg-[#E63946] text-white text-xs uppercase tracking-wider hover:bg-[#E63946]/80 transition-colors flex items-center gap-1.5" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="save-profile-btn"><Check className="w-3.5 h-3.5" /> Save</button>
                    <button onClick={() => setEditing(false)} className="px-4 py-2 text-zinc-400 text-xs uppercase tracking-wider hover:text-white transition-colors flex items-center gap-1.5" style={{ fontFamily: 'Bebas Neue, sans-serif' }}><X className="w-3.5 h-3.5" /> Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <h1 className="text-white text-2xl sm:text-3xl mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="profile-display-name">{profile.display_name}</h1>
                  <span className="text-zinc-500 text-sm" data-testid="profile-username">@{profile.username}</span>
                  {profile.bio && <p className="text-zinc-400 text-sm mt-3 leading-relaxed" data-testid="profile-bio">{profile.bio}</p>}
                  <div className="flex items-center gap-5 mt-4">
                    <span className="text-zinc-500 text-xs"><strong className="text-white">{profile.followers_count || 0}</strong> followers</span>
                    <span className="text-zinc-500 text-xs"><strong className="text-white">{profile.following_count || 0}</strong> following</span>
                    <span className="text-zinc-500 text-xs"><strong className="text-white">{profile.posts_count || 0}</strong> posts</span>
                  </div>
                </>
              )}
            </div>

            <div className="flex-shrink-0">
              {/* eslint-disable-next-line no-nested-ternary -- readable tier picker */}
              {isOwnProfile ? (
                !editing && (
                  <button onClick={() => setEditing(true)} className="px-4 py-2 border border-zinc-700 text-zinc-300 text-xs uppercase tracking-wider hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors flex items-center gap-1.5" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="edit-profile-btn"><Edit3 className="w-3.5 h-3.5" /> Edit Profile</button>
                )
              ) : user ? (
                <button onClick={handleFollow}
                  className={`px-5 py-2.5 text-xs uppercase tracking-wider transition-colors flex items-center gap-1.5 ${isFollowing ? 'border border-zinc-700 text-zinc-300 hover:border-red-500/50 hover:text-red-400' : 'bg-[#E63946] text-white hover:bg-[#E63946]/80'}`}
                  style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="profile-follow-btn"
                >{isFollowing ? <><UserMinus className="w-3.5 h-3.5" /> Unfollow</> : <><UserPlus className="w-3.5 h-3.5" /> Follow</>}</button>
              ) : null}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-zinc-800" data-testid="profile-tabs">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 text-xs uppercase tracking-wider transition-colors ${activeTab === tab.id ? 'text-[#E63946] border-b-2 border-[#E63946]' : 'text-zinc-500 hover:text-zinc-300'}`}
              style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid={`profile-tab-${tab.id}`}
            >{tab.label} <span className="text-zinc-600 ml-1">{tab.count}</span></button>
          ))}
        </div>

        {activeTab === 'posts' && (
          <div className="space-y-4">
            {activities.length > 0 ? activities.map((a, i) => (
              <ProfileActivityCard key={a.activity_id || `${a.created_at}-${i}`} activity={a} token={token} user={user} />
            )) : (
              <div className="text-center py-16 bg-zinc-900/40 border border-zinc-800">
                <Activity className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500 text-sm">No activity yet.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'followers' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {followers.length > 0 ? followers.map(p => (
              <Link key={p.username} to={`/community/${p.username}`} className="flex items-center gap-3 bg-zinc-900/60 border border-zinc-800 p-4 hover:border-zinc-700 transition-colors" data-testid={`follower-${p.username}`}>
                <div className="w-10 h-10 bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-400 text-sm font-bold uppercase flex-shrink-0" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  {p.avatar ? <img src={p.avatar} alt="" className="w-full h-full object-cover" /> : p.display_name?.charAt(0) || '?'}
                </div>
                <div><span className="text-white text-sm block">{p.display_name}</span><span className="text-zinc-500 text-xs">@{p.username}</span></div>
              </Link>
            )) : <p className="text-zinc-500 text-sm col-span-2 text-center py-12">No followers yet.</p>}
          </div>
        )}

        {activeTab === 'following' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {following.length > 0 ? following.map(p => (
              <Link key={p.username} to={`/community/${p.username}`} className="flex items-center gap-3 bg-zinc-900/60 border border-zinc-800 p-4 hover:border-zinc-700 transition-colors" data-testid={`following-${p.username}`}>
                <div className="w-10 h-10 bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-400 text-sm font-bold uppercase flex-shrink-0" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  {p.avatar ? <img src={p.avatar} alt="" className="w-full h-full object-cover" /> : p.display_name?.charAt(0) || '?'}
                </div>
                <div><span className="text-white text-sm block">{p.display_name}</span><span className="text-zinc-500 text-xs">@{p.username}</span></div>
              </Link>
            )) : <p className="text-zinc-500 text-sm col-span-2 text-center py-12">Not following anyone yet.</p>}
          </div>
        )}
      </div>
    </main>
  );
}
